// SPDX-License-Identifier: AGPL-3.0-or-later

import {nameStyleFontFamily, nameStyleFontKey} from '@app/features/name_style/NameStyleFonts';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {
	isNameColorsReadableOnLight,
	type Lab,
	labDeltaE2000,
	NAME_COLOR_ROLE_MIN_DELTA_E,
	srgbToLab,
} from '@fluxer/constants/src/ColorContrast';
import type {NameStyle} from '@fluxer/schema/src/domains/user/PlusAppearanceSchemas';

export interface NameStyleView {
	fontKey: string | null;
	fontFamily: string | null;
	solid: string | null;
	gradient: readonly [string, string] | null;
	// The colours fail 3:1 on the light theme, which then draws the surface's default colour (a CSS rule on
	// html.theme-light, so a theme switch re-renders nothing).
	defaultOnLight: boolean;
}

export type GuildRoleColors = Readonly<
	Record<
		string,
		{
			readonly color: number;
			readonly colorSecondary?: number | null;
			readonly position: number;
			readonly permissions: bigint;
			readonly isEveryone?: boolean;
		}
	>
>;

export interface NameStyleContext {
	// The surface keeps its own colour (a profile theme the colours cannot be read on); only the font is drawn.
	fontOnly?: boolean;
	// The roles of the guild the name is drawn in; outside a guild the whole style is drawn.
	guildRoles?: GuildRoleColors;
	// The role ids the guild's owner holds; undefined while the owner's member is not loaded.
	guildOwnerRoleIds?: ReadonlySet<string>;
	// The highest staff role position the name's member holds (Infinity for the guild's owner): staff roles at or below
	// it are the member's own standing and do not hold the colours back. Undefined for a member who is not staff.
	staffPosition?: number;
}

const MAX_DECISIONS_PER_ROLES_VERSION = 2048;

// A role with any of these runs the guild; its colour is the one a name could be mistaken for. Only guild-wide role
// permissions count: a staff permission granted by a channel permission overwrite is not considered.
const STAFF_PERMISSIONS =
	Permissions.ADMINISTRATOR |
	Permissions.MANAGE_GUILD |
	Permissions.MANAGE_ROLES |
	Permissions.MANAGE_CHANNELS |
	Permissions.KICK_MEMBERS |
	Permissions.BAN_MEMBERS |
	Permissions.MODERATE_MEMBERS |
	Permissions.MANAGE_MESSAGES |
	Permissions.MUTE_MEMBERS |
	Permissions.DEAFEN_MEMBERS |
	Permissions.MOVE_MEMBERS |
	Permissions.MANAGE_NICKNAMES;

export function colorToHex(color: number): string {
	return `#${color.toString(16).padStart(6, '0')}`;
}

function isColor(value: number): boolean {
	return Number.isInteger(value) && value >= 0 && value <= 0xffffff;
}

interface StaffRoleColors {
	position: number;
	labs: ReadonlyArray<Lab>;
}

// A name passes for a role only when each colour it is drawn from (a solid colour, or both ends of a gradient) sits next
// to a colour of that same role; a gradient with one end near a role colour reads as a different name.
function clashes(colors: ReadonlyArray<number>, roles: ReadonlyArray<StaffRoleColors>, ownPosition: number): boolean {
	if (colors.length === 0) return false;
	const ends = colors.map(srgbToLab);
	return roles.some(
		(role) =>
			role.position > ownPosition &&
			ends.every((end) => role.labs.some((lab) => labDeltaE2000(end, lab) < NAME_COLOR_ROLE_MIN_DELTA_E)),
	);
}

// A name this close to a coloured role could pass for that role, a moderator's red for one.
export function nameColorsClashWithRoles(colors: ReadonlyArray<number>, roleColors: ReadonlyArray<number>): boolean {
	const roles = roleColors.map((color) => ({position: 0, labs: [srgbToLab(color)]}));
	return clashes(colors, roles, Number.NEGATIVE_INFINITY);
}

// The highest position among the roles with a staff permission the member holds, coloured or not; @everyone never
// counts. Undefined for a member holding none.
export function staffRolePosition(roles: GuildRoleColors, memberRoleIds: Iterable<string>): number | undefined {
	let highest: number | undefined;
	for (const roleId of memberRoleIds) {
		const role = roles[roleId];
		if (!role || role.isEveryone || (role.permissions & STAFF_PERMISSIONS) === 0n) continue;
		if (highest === undefined || role.position > highest) highest = role.position;
	}
	return highest;
}

interface RoleColorDecisions {
	ownerRoleIds: ReadonlySet<string> | undefined;
	staffRoles: ReadonlyArray<StaffRoleColors>;
	byColors: Map<string, boolean>;
}

// Keyed on the guild's roles record: the guild model replaces the record whenever the guild changes, so a new record is
// the next role version and the old decisions are collected with the old record. The owner's role set is checked by
// identity: the member model is replaced on every member update, so a new set means the owner's roles may have changed.
const decisionsByRoles = new WeakMap<GuildRoleColors, RoleColorDecisions>();

// The owner holds every permission, so each coloured role the owner holds counts as a staff role. Without the owner's
// member loaded, only the roles' own permissions decide. Staff roles at or below the name member's own staff position
// are left out.
export function nameColorsClashInGuild(
	roles: GuildRoleColors,
	colors: ReadonlyArray<number>,
	ownerRoleIds?: ReadonlySet<string>,
	staffPosition?: number,
): boolean {
	let decisions = decisionsByRoles.get(roles);
	if (!decisions || decisions.ownerRoleIds !== ownerRoleIds) {
		const staffRoles: Array<StaffRoleColors> = [];
		for (const [roleId, role] of Object.entries(roles)) {
			if (!role.color || role.isEveryone) continue;
			if ((role.permissions & STAFF_PERMISSIONS) === 0n && !ownerRoleIds?.has(roleId)) continue;
			const labs = [srgbToLab(role.color)];
			if (role.colorSecondary != null) labs.push(srgbToLab(role.colorSecondary));
			staffRoles.push({position: role.position, labs});
		}
		decisions = {ownerRoleIds, staffRoles, byColors: new Map()};
		decisionsByRoles.set(roles, decisions);
	}
	const key = `${colors.join(',')}|${staffPosition ?? ''}`;
	let clash = decisions.byColors.get(key);
	if (clash === undefined) {
		clash = clashes(colors, decisions.staffRoles, staffPosition ?? Number.NEGATIVE_INFINITY);
		if (decisions.byColors.size >= MAX_DECISIONS_PER_ROLES_VERSION) decisions.byColors.clear();
		decisions.byColors.set(key, clash);
	}
	return clash;
}

// The font always shows. The colours give way to the surface's own colour where the surface asks for the font alone
// and where they could pass for a coloured staff role of the guild above the member's own standing; on the light theme
// also when they fail 3:1 there.
export function resolveNameStyleView(style: NameStyle | null, context: NameStyleContext): NameStyleView | null {
	if (!style) return null;
	const fontKey = nameStyleFontKey(style.font_id);
	const colors = style.colors.filter(isColor).slice(0, 2);
	const defaultColor =
		colors.length === 0 ||
		context.fontOnly === true ||
		(context.guildRoles !== undefined &&
			nameColorsClashInGuild(context.guildRoles, colors, context.guildOwnerRoleIds, context.staffPosition));
	const hex = defaultColor ? [] : colors.map(colorToHex);
	if (fontKey === null && hex.length === 0) return null;
	return {
		fontKey,
		fontFamily: fontKey === null ? null : nameStyleFontFamily(fontKey),
		solid: hex.length === 1 ? hex[0] : null,
		gradient: hex.length === 2 ? [hex[0], hex[1]] : null,
		defaultOnLight: hex.length > 0 && !isNameColorsReadableOnLight(colors),
	};
}
